import { useRef, useLayoutEffect, useState, useMemo } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getUseCasesCount } from "./use-cases-registry";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export function useScrollTransition() {
  const useCasesCount = getUseCasesCount();

  // Refs for GSAP targeting
  const sectionRef = useRef<HTMLDivElement>(null);
  const rightBarRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);

  // State to track which use case is currently active (by index)
  const [activeUseCaseIndex, setActiveUseCaseIndex] = useState(0);

  // Create refs array for all video containers
  const videoRefs = useMemo(() => {
    return Array.from({ length: useCasesCount }, () => ({
      current: null as HTMLDivElement | null,
    }));
  }, [useCasesCount]);

  useLayoutEffect(() => {
    if (!sectionRef.current || useCasesCount < 2) {
      return;
    }

    const section = sectionRef.current;
    const videoElements = videoRefs
      .map((ref) => ref.current)
      .filter(Boolean) as HTMLDivElement[];

    if (videoElements.length < 2) return;

    // Set initial positions - first video visible, rest below
    videoElements.forEach((video, index) => {
      gsap.set(video, {
        y: index === 0 ? 0 : "100%",
        zIndex: useCasesCount - index,
      });
    });

    // Calculate scroll distance based on number of use cases
    // Each transition takes 100% viewport height
    const totalScrollDistance = (useCasesCount - 1) * 100;

    // Create the main timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "center center",
        end: `+=${totalScrollDistance}%`,
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        onUpdate: (self) => {
          // Calculate which use case should be active based on scroll progress
          const progressPerCase = 1 / (useCasesCount - 1);
          const newIndex = Math.min(
            Math.floor(self.progress / progressPerCase + 0.5),
            useCasesCount - 1,
          );

          setActiveUseCaseIndex((current) => {
            if (current !== newIndex) {
              return newIndex;
            }
            return current;
          });
        },
        invalidateOnRefresh: true,
      },
    });

    // Add transition animations for each use case pair
    const transitionDuration = 1 / (useCasesCount - 1);

    for (let i = 0; i < useCasesCount - 1; i++) {
      const currentVideo = videoElements[i];
      const nextVideo = videoElements[i + 1];
      const startTime = i * transitionDuration;

      // Current video slides up and out
      tl.to(
        currentVideo,
        {
          y: "-100%",
          duration: transitionDuration,
          ease: "power2.inOut",
        },
        startTime,
      );

      // Next video slides up to replace it
      tl.to(
        nextVideo,
        {
          y: "0%",
          duration: transitionDuration,
          ease: "power2.inOut",
        },
        startTime,
      );
    }

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger === section) {
          trigger.kill();
        }
      });
      tl.kill();
    };
  }, [useCasesCount, videoRefs]);

  // Additional effect to handle window resize
  useLayoutEffect(() => {
    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return {
    sectionRef,
    videoRefs,
    rightBarRef,
    bottomBarRef,
    activeUseCaseIndex,
    useCasesCount,
  };
}
